import { useRef, useState, useEffect } from 'react';
import Card from '@material-ui/core/Card';
import CardActions from '@material-ui/core/CardActions';
import CardContent from '@material-ui/core/CardContent';
import CardMedia from '@material-ui/core/CardMedia';
import Chip from '@material-ui/core/Chip';
import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';
import IconButton from '@material-ui/core/IconButton';
import Tooltip from '@material-ui/core/Tooltip';
import Link from '@material-ui/core/Link';
import { makeStyles } from '@material-ui/core/styles';
import StarIcon from '@material-ui/icons/Star';
import StarBorderIcon from '@material-ui/icons/StarBorder';
import { ItemCardHeader, LinkButton } from '@backstage/core-components';
import { useApi } from '@backstage/core-plugin-api';
import {
  starredEntitiesApiRef,
  useStarredEntities,
} from '@backstage/plugin-catalog-react';

const MAX_VISIBLE_TAGS = 6;

const useStyles = makeStyles({
  card: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
  },
  content: {
    flex: 1,
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
  },
  description: {
    display: '-webkit-box',
    WebkitBoxOrient: 'vertical',
    WebkitLineClamp: 4,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
});

export interface AcceleratorCardProps {
  title: string;
  description: string;
  type?: string;
  tags?: string[];
  detailsHref: string;
  launchHref: string;
  entityRef?: string;
}

export const AcceleratorCard = ({
  title,
  description,
  type,
  tags,
  detailsHref,
  launchHref,
  entityRef,
}: AcceleratorCardProps) => {
  const classes = useStyles();
  const descriptionRef = useRef<HTMLSpanElement>(null);
  const [isTruncated, setIsTruncated] = useState(false);
  const [tagsExpanded, setTagsExpanded] = useState(false);
  const starredEntitiesApi = useApi(starredEntitiesApiRef);
  const { starredEntities } = useStarredEntities();
  const isStarred = entityRef ? starredEntities.has(entityRef) : false;

  useEffect(() => {
    const el = descriptionRef.current;
    if (el) {
      setIsTruncated(el.scrollHeight > el.clientHeight);
    }
  }, [description]);

  const visibleTags = tagsExpanded ? tags : tags?.slice(0, MAX_VISIBLE_TAGS);
  const overflowCount = tags ? tags.length - MAX_VISIBLE_TAGS : 0;

  const descriptionContent = (
    <Typography
      ref={descriptionRef}
      variant="body2"
      color="textSecondary"
      className={classes.description}
    >
      {description}
    </Typography>
  );

  return (
    <Card className={classes.card}>
      <CardMedia style={{ position: 'relative' }}>
        <ItemCardHeader title={title} subtitle={type} />
        {entityRef && (
          <IconButton
            size="small"
            onClick={() => starredEntitiesApi.toggleStarred(entityRef)}
            style={{
              position: 'absolute',
              top: 8,
              right: 8,
              color: isStarred ? '#f3ba37' : 'rgba(255,255,255,0.7)',
            }}
          >
            {isStarred ? <StarIcon /> : <StarBorderIcon />}
          </IconButton>
        )}
      </CardMedia>
      <CardContent className={classes.content}>
        {visibleTags?.length ? (
          <Box mb={1}>
            {visibleTags.map(tag => (
              <Chip
                key={tag}
                size="small"
                label={tag}
                style={{ marginRight: 4, marginBottom: 4 }}
              />
            ))}
            {overflowCount > 0 && (
              <Chip
                size="small"
                label={tagsExpanded ? 'show less' : `+${overflowCount} more`}
                variant="outlined"
                clickable
                onClick={() => setTagsExpanded(prev => !prev)}
                style={{ marginRight: 4, marginBottom: 4 }}
              />
            )}
          </Box>
        ) : null}
        {isTruncated ? (
          <Tooltip title={description} placement="top">
            <div>
              {descriptionContent}
              <Link href={detailsHref} variant="body2">
                read more
              </Link>
            </div>
          </Tooltip>
        ) : (
          descriptionContent
        )}
      </CardContent>
      <CardActions>
        <LinkButton to={detailsHref} color="primary">
          Explore
        </LinkButton>
        <LinkButton to={launchHref} color="primary">
          Launch
        </LinkButton>
      </CardActions>
    </Card>
  );
};
